/**
 * BLRRD Editor — Service Worker registration + update toast.
 *
 * Manual registration (no virtual:pwa-register) so the file works whether
 * loaded through Vite or hand-served. We listen for `updatefound` and pop
 * a confirm-toast; the SW only skips waiting after the user clicks.
 *
 * Strict no-innerHTML — DOM is built with createElement + textContent so
 * the security lint stays clean.
 */

const SW_URL   = '/sw.js';
const SW_TYPE  = 'classic';
const SW_SCOPE = '/';

/* Dev server doesn't ship a SW (vite-plugin-pwa devOptions.enabled=false to
 * keep the strict CSP intact). Skip registration so the console stays clean
 * during iteration. The PWA gate (scripts/test-pwa.mjs) tests against the
 * built output via `vite preview`. */
const IS_DEV = !!(import.meta && import.meta.env && import.meta.env.DEV);

/* Heuristics: don't register on file://, blob:, or non-secure remote hosts.
 * localhost / 127.0.0.1 are allowed (browsers treat them as secure). */
function isSWEligible() {
    if (!('serviceWorker' in navigator)) return false;
    const { protocol, hostname } = window.location;
    if (protocol === 'https:') return true;
    if (protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]')) return true;
    return false;
}

function buildToast(onConfirm, onDismiss) {
    const root = document.createElement('div');
    root.id = 'pwaUpdateToast';
    root.className = 'pwa-toast';
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');

    const msg = document.createElement('span');
    msg.className = 'pwa-toast-msg';
    msg.textContent = 'Update bereit';

    const reload = document.createElement('button');
    reload.type = 'button';
    reload.className = 'pwa-toast-btn pwa-toast-btn-primary';
    reload.textContent = 'Neu laden';
    reload.addEventListener('click', onConfirm);

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'pwa-toast-btn';
    dismiss.setAttribute('aria-label', 'Später');
    dismiss.textContent = 'Später';
    dismiss.addEventListener('click', onDismiss);

    root.append(msg, reload, dismiss);
    return root;
}

function showUpdateToast(reg) {
    /* Idempotent — never stack multiple toasts. */
    document.getElementById('pwaUpdateToast')?.remove();

    const dismiss = () => document.getElementById('pwaUpdateToast')?.remove();

    const confirm = () => {
        const sw = reg.waiting;
        if (!sw) { dismiss(); return; }
        /* One-shot reload after the new SW takes control. */
        const onControllerChange = () => {
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
            window.location.reload();
        };
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        sw.postMessage({ type: 'SKIP_WAITING' });
    };

    const toast = buildToast(confirm, dismiss);
    document.body.appendChild(toast);
}

function watchForUpdates(reg) {
    /* Already-waiting SW from a previous load. */
    if (reg.waiting && navigator.serviceWorker.controller) {
        showUpdateToast(reg);
    }
    reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
                showUpdateToast(reg);
            }
        });
    });
}

/* Async, fire-and-forget. Errors stay non-fatal — editor must work even when
 * SW registration fails (private mode, dev override, no-PWA browsers). */
export async function initPWA() {
    if (IS_DEV) return null;
    if (!isSWEligible()) return null;
    try {
        /* type:'module' for dev-mode SW (Workbox imports via ESM); production
         * is a classic bundle. */
        const reg = await navigator.serviceWorker.register(SW_URL, {
            scope: SW_SCOPE,
            type:  SW_TYPE,
        });
        watchForUpdates(reg);
        /* Periodic update check — also triggers on focus. */
        setInterval(() => { reg.update().catch(() => {}); }, 60 * 60 * 1000);
        window.addEventListener('focus', () => { reg.update().catch(() => {}); });
        return reg;
    } catch (err) {
        console.warn('[blrrd] SW registration failed:', err);
        return null;
    }
}
